import React from 'react';
import { Helmet } from 'react-helmet-async';
import { client } from '../lib/sanity';
import type { FinancialReport } from '../types/sanity';
import { FinancialReportsHero } from '../components/financialReports/FinancialReportsHero';
import { ReportsList } from '../components/financialReports/ReportsList';
import { ReportsInfo } from '../components/financialReports/ReportsInfo';
import { LoadingMessage } from '../components/common/LoadingMessage';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { FundingProjectCard } from '../components/financialReports/FundingProjectCard';

export function FinancialReportsPage() {
  const [reports, setReports] = React.useState<FinancialReport[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedYear, setSelectedYear] = React.useState<number | null>(null);
  const [selectedType, setSelectedType] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchReports = async () => {
      try {
        const result = await client.fetch<FinancialReport[]>(`
          *[_type == "financialReport" && isPublic == true && !(_id in path("drafts.**"))] | order(year desc, publishedAt desc) {
            _id,
            title,
            year,
            reportType,
            description,
            publishedAt,
            "file": file.asset->{
              _ref,
              url
            },
            isPublic
          }
        `);
        
        setReports(result);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching financial reports:', err);
        setError('Nie udało się załadować sprawozdań');
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  // Get unique years from reports
  const years = React.useMemo(() => {
    const uniqueYears = [...new Set(reports.map(report => report.year))];
    return uniqueYears.sort((a, b) => b - a); // Sort descending
  }, [reports]);

  // Get unique report types from reports
  const reportTypes = React.useMemo(() => {
    const uniqueTypes = [...new Set(reports.map(report => report.reportType))];
    return uniqueTypes;
  }, [reports]);

  // Filter reports based on selected year and type
  const filteredReports = React.useMemo(() => {
    return reports.filter(report => {
      const matchesYear = selectedYear ? report.year === selectedYear : true;
      const matchesType = selectedType ? report.reportType === selectedType : true;
      return matchesYear && matchesType;
    });
  }, [reports, selectedYear, selectedType]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <Helmet>
        <title>Sprawozdania | Fundacja "Znajdki"</title>
        <meta name="description" content="Sprawozdania finansowe i z działalności Fundacji Znajdki. Przejrzystość i odpowiedzialność w naszych działaniach." />
      </Helmet>

      <FinancialReportsHero />

      {/* W prostym języku */}
      <section className="mb-8">
        <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-6 rounded-lg shadow-lg">
          <h2 className="heading-4 no-margin">W skrócie</h2>
          <p className="body-medium no-margin mt-2">Tutaj znajdziesz nasze sprawozdania finansowe i merytoryczne. Możesz filtrować po roku i typie dokumentu.</p>
        </div>
      </section>

      {/* Projekty dofinansowane */}
      <section className="mb-8">
        <FundingProjectCard />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {loading ? (
            <LoadingMessage ariaLabel="Ładowanie sprawozdań" />
          ) : error ? (
            <ErrorMessage message={error} ariaLabel="Błąd ładowania sprawozdań" />
          ) : (
            <ReportsList 
              reports={filteredReports}
              loading={false}
              error={null}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              selectedType={selectedType}
              setSelectedType={setSelectedType}
              years={years}
              reportTypes={reportTypes}
            />
          )}
        </div>
        
        <div className="lg:col-span-1">
          <ReportsInfo />
        </div>
      </div>
    </div>
  );
}